import { jwtDecode } from 'jwt-decode'
import { defineStore } from 'pinia'
import { reactive } from 'vue'
import { useOscarClient } from '~/composables/useOscarClient'

const TRAILING_SLASH_RE = /\/$/

export const useAuthStore = defineStore('auth', () => {
  let keycloak = null
  const router = useRouter()

  const authenticated = ref(false)
  const registered = ref(false)
  const token = reactive({
    access: null,
    refresh: null,
  })

  const user = reactive({
    email: null,
    firstName: null,
    lastName: null,
    phone: null,
    birthday: null,
    id: null,
    invoicePermitted: null,
    specialPrice: null,
    customerDiscount: null,
  })

  const specialPrice = computed(() => {
    return user.specialPrice
  })

  const hash = function (s) {
    let h = 0xDEADBEEF
    for (let i = 0; i < s.length; i++) {
      h = Math.imul(h ^ s.charCodeAt(i), 2654435761)
    }

    return (h ^ (h >>> 16)) >>> 0
  }

  const settings = ref(null)

  function updateSettings(locale) {
    if (!locale) {
      locale = 'de'
    }

    const oscar = useOscarClient(locale)

    oscar.getSettings().then((res) => {
      settings.value = res.data
    })
  }

  async function updateUser(locale) {
    const config = useRuntimeConfig()
    const baseURL = config.public.apiBase?.replace(TRAILING_SLASH_RE, '') || ''
    const response = await $fetch(`${baseURL}/user/`, {
      credentials: 'include',
      headers: {
        'Accept-Language': `${locale}-${locale.toUpperCase()}`,
        ...(token.access ? { 'x-forwarded-access-token': token.access } : {}),
      },
    })

    if (response && 'email' in response) {
      user.email = response.email
      user.firstName = response.first_name
      user.lastName = response.last_name
      user.phone = response.phone
      user.birthday = response.birth_date
      user.invoicePermitted = response.invoice_permitted
      user.specialPrice = response.has_special_prices || false
      user.customerDiscount = response.customer_discount
    }

    if (user.firstName && user.lastName) {
      registered.value = true
    }

    return user
  }

  function getCSRFToken() {
    const config = useRuntimeConfig()
    const baseURL = (false && config.serverApiBase)
      ? config.serverApiBase
      : (config.public.apiBase ?? '')

    const url = `${(baseURL || '').replace(TRAILING_SLASH_RE, '')}/user/`
    return $fetch(url, { credentials: 'include' })
  }

  async function init(locale) {
    const Keycloak = (await import('keycloak-js')).default
    if (keycloak) {
      return
    }

    if (!locale) {
      locale = 'de'
    }

    // client side only
    try {
      const client = true

      if (!client) {
        return
      }
    }
    catch (e) {
      console.error(e)
      return
    }

    const config = useRuntimeConfig()
    getCSRFToken()

    // init keycloak
    if (keycloak === null) {
      keycloak = new Keycloak({
        url: config.public.keycloakBase,
        realm: 'LUMA',
        clientId: 'luma-onlineshop',
      })
    }

    refreshTokenHook(keycloak)
    token.access = localStorage.getItem('access')
    token.refresh = localStorage.getItem('refresh')

    keycloak.onAuthSuccess = () => {
      if (localStorage.getItem('loginTry') === '1') {
        localStorage.removeItem('loginTry')
        router.replace({ query: { alert: 'loggedIn' } })
      }
    }

    const data = {
      checkLoginIframe: false,
    }

    if (token.access && token.refresh) {
      Object.assign(data, {
        token: token.access,
        refreshToken: token.refresh,
      })
    }

    if (!accessTokenValid()) {
      if (!refreshTokenValid()) {
        if (token.access && token.refresh) {
          const oscar = useOscarClient()
          await oscar.logout()
        }

        localStorage.removeItem('access')
        localStorage.removeItem('refresh')
      }
    }

    await keycloak.init(data).catch((e) => {
      // eslint-disable-next-line no-console
      console.debug(e)
    })

    authenticated.value = keycloak.authenticated

    if (keycloak.token && keycloak.refreshToken) {
      token.access = keycloak.token
      token.refresh = keycloak.refreshToken
      localStorage.setItem('access', token.access)
      localStorage.setItem('refresh', token.refresh)
    }

    // registered
    if (authenticated.value) {
      await updateUser(locale)

      const profile = await keycloak.loadUserProfile()
      user.id = hash(profile.id) // Hash for  Analytics Tracking

      const oscar = useOscarClient(locale)
      oscar.getMostPurchased()
    }

    // middleware
    useState('authenticated', () => authenticated.value)
    useState('registered', () => registered.value)
  }

  function refreshTokenHook(keycloak) {
    keycloak.onTokenExpired = () => {
      keycloak
        .updateToken(30)
        .then(() => {
          localStorage.setItem('access', keycloak.token)
          localStorage.setItem('refresh', keycloak.refreshToken)
        })
        .catch((_err) => {
          localStorage.removeItem('access')
          localStorage.removeItem('refresh')
        })
    }
  }

  function accessTokenValid() {
    const token = localStorage.getItem('access')
    return jwtTokenValid(token)
  }

  function jwtTokenValid(token) {
    if (!token) {
      return false
    }

    try {
      const decoded_token = jwtDecode(token)
      const exp = decoded_token.exp

      if (Date.now() >= exp * 1000) {
        return false
      }
    }
    catch (err) {
      console.error(err)
      return false
    }

    return true
  }

  function refreshTokenValid() {
    const token = localStorage.getItem('refresh')
    return jwtTokenValid(token)
  }

  function withKeycloakLocale(url, locale) {
    if (!locale) {
      return url
    }

    const localizedUrl = new URL(url, window.location.origin)
    localizedUrl.searchParams.set('ui_locales', locale)
    localizedUrl.searchParams.set('kc_locale', locale)
    return localizedUrl.toString()
  }

  function normalizeKeycloakLocale(locale) {
    return ['de', 'fr'].includes(locale) ? locale : null
  }

  async function syncKeycloakLocale(locale) {
    const normalizedLocale = normalizeKeycloakLocale(locale)
    if (!normalizedLocale || !keycloak?.authenticated) {
      return
    }

    try {
      await keycloak.updateToken(30)

      if (keycloak.token && keycloak.refreshToken) {
        token.access = keycloak.token
        token.refresh = keycloak.refreshToken
        localStorage.setItem('access', token.access)
        localStorage.setItem('refresh', token.refresh)
      }

      if (!token.access) {
        return
      }

      const config = useRuntimeConfig()
      const keycloakBase = config.public.keycloakBase?.replace(TRAILING_SLASH_RE, '')
      if (!keycloakBase) {
        return
      }

      const accountUrl = `${keycloakBase}/realms/LUMA/account`
      const profile = await $fetch(accountUrl, {
        query: {
          userProfileMetadata: false,
        },
        headers: {
          Authorization: `Bearer ${token.access}`,
        },
      })

      const attributes = {
        ...(profile.attributes ?? {}),
      }
      const currentLocale = Array.isArray(attributes.locale)
        ? attributes.locale[0]
        : attributes.locale

      if (currentLocale === normalizedLocale) {
        return
      }

      await $fetch(accountUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token.access}`,
        },
        body: {
          ...profile,
          attributes: {
            ...attributes,
            locale: [normalizedLocale],
          },
        },
      })
    }
    catch (error) {
      console.warn('Failed to sync Keycloak locale', error)
    }
  }

  async function login(redirectUri, locale) {
    localStorage.setItem('loginTry', '1')

    if (!keycloak) {
      await init()
    }

    const options = {}
    if (redirectUri) {
      options.redirectUri = redirectUri
    }

    options.locale = locale
    window.location.assign(withKeycloakLocale(keycloak.createLoginUrl(options), locale))
  }

  async function logout() {
    if (!keycloak) {
      await init()
    }

    authenticated.value = false
    localStorage.removeItem('access')
    localStorage.removeItem('refresh')
    token.access = null
    token.refresh = null

    keycloak.logout({
      redirectUri: location.origin,
    })

    const oscar = useOscarClient()
    await oscar.logout()
  }

  async function register(redirectUrl, locale) {
    if (!keycloak) {
      await init()
    }

    const options = {}
    if (redirectUrl) {
      options.redirectUri = redirectUrl || location.href
    }

    options.locale = locale
    return withKeycloakLocale(keycloak.createRegisterUrl(options), locale)
  }

  async function passwordReset(locale) {
    if (!keycloak) {
      await init()
    }

    localStorage.setItem('passwordResetInit', 1)
    await syncKeycloakLocale(locale)

    return withKeycloakLocale(keycloak.createLoginUrl({
      action: 'UPDATE_PASSWORD',
      redirectUri: location.href,
      locale,
    }), locale)
  }

  async function account(locale) {
    if (!keycloak) {
      await init()
    }

    await syncKeycloakLocale(locale)

    return withKeycloakLocale(keycloak.createLoginUrl({
      action: 'UPDATE_PROFILE',
      redirectUri: location.href,
      locale,
    }), locale)
  }

  return {
    authenticated,
    registered,
    token,
    user,
    settings,
    specialPrice,
    init,
    passwordReset,
    login,
    register,
    logout,
    account,
    updateSettings,
    updateUser,
  }
})
